import { useEffect, type ReactNode } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Toaster, toast } from 'sonner'
import { BookOpen, FileUp, Home, LogOut, MessageSquare, Settings2, Shield, Users } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { SharedProps } from '@/types'

const NAV = [
  { href: '/', label: 'Home', icon: Home, match: (url: string) => url === '/' },
  { href: '/lessons', label: 'My lessons', icon: BookOpen, match: (url: string) => url.startsWith('/lessons') || url.startsWith('/materials') },
  { href: '/imports/new', label: 'Import', icon: FileUp, match: (url: string) => url.startsWith('/import') },
  { href: '/class_profiles', label: 'Classes', icon: Users, match: (url: string) => url.startsWith('/class_profiles') },
]

export default function AppLayout({ children }: { children: ReactNode }) {
  const page = usePage<SharedProps>()
  const { auth, config, usage } = page.props
  const flash = page.flash as { notice?: string; alert?: string } | undefined

  useEffect(() => {
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) toast.error(flash.alert)
  }, [flash?.notice, flash?.alert])

  return (
    <div className="min-h-screen">
      <header className="no-print sticky top-0 z-30 border-b border-line bg-paper/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-6 px-4 sm:px-6">
          <Link href="/" className="flex items-center gap-2 font-serif text-xl font-semibold text-brand-700">
            <img src="/icon.svg" alt="" className="size-7" />
            Lessy
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map(({ href, label, icon: Icon, match }) => (
              <Link
                key={href}
                href={href}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-muted hover:bg-stone-100 hover:text-ink',
                  match(page.url) && 'bg-white text-ink shadow-sm ring-1 ring-line',
                )}
              >
                <Icon className="size-4" />
                {label}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {config.ai_mode === 'offline' && (
              <span className="hidden rounded-full bg-sun-50 px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-200 sm:inline" title="No ANTHROPIC_API_KEY: Lessy is returning sample content.">
                Offline AI mode
              </span>
            )}
            {usage && usage.lessons.limit !== null && (
              <span className="hidden text-xs text-muted lg:inline">
                {usage.lessons.limit - usage.lessons.used} of {usage.lessons.limit} lessons left this month
              </span>
            )}
            {auth && (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex size-8 items-center justify-center rounded-full bg-brand-700 text-sm font-semibold text-white">
                  {auth.user.name.charAt(0).toUpperCase()}
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <div className="px-2 py-1.5 text-xs text-muted">{auth.user.email}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => router.visit('/class_profiles')}>
                    <Settings2 /> Class profiles
                  </DropdownMenuItem>
                  {config.feedback_url && (
                    <DropdownMenuItem onSelect={() => window.open(config.feedback_url!, '_blank', 'noopener')}>
                      <MessageSquare /> Send feedback
                    </DropdownMenuItem>
                  )}
                  {auth.user.admin && (
                    <DropdownMenuItem onSelect={() => router.visit('/admin/generations')}>
                      <Shield /> Generation costs
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => router.delete('/session')}>
                    <LogOut /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto border-t border-line px-4 py-1.5 md:hidden">
          {NAV.map(({ href, label, match }) => (
            <Link key={href} href={href} className={cn('whitespace-nowrap rounded-md px-3 py-1 text-sm text-muted', match(page.url) && 'bg-white text-ink ring-1 ring-line')}>
              {label}
            </Link>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">{children}</main>
      {config.feedback_url && (
        <a
          href={config.feedback_url}
          target="_blank"
          rel="noopener"
          className="no-print fixed bottom-4 right-4 z-20 flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm text-white shadow-lg hover:bg-ink/90"
        >
          <MessageSquare className="size-4" /> Feedback
        </a>
      )}
      <Toaster position="bottom-left" richColors closeButton />
    </div>
  )
}
